import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { queryKeys } from '../../../queries/keys';
import { VideoOperationService } from '../../../services';
import type {
  VideoLabOperationRequest,
  VideoOperationResult,
  VideoOperationStatus,
  VideoOperationType,
} from '../../../domain';
import { useStudioRequest } from './studioRequestContext';
import { VideoOperationFailedError } from './videoOperationFailedError';
import { useRequireSignIn } from '../../../components/auth/useRequireSignIn';

const TERMINAL: readonly VideoOperationStatus[] = ['Completed', 'Failed', 'Cancelled'];

const isTerminal = (s?: VideoOperationStatus) => !!s && TERMINAL.includes(s);

/** A fresh idempotency token, with a fallback for environments lacking crypto.randomUUID. */
function newIdempotencyKey(): string {
  const c = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return `vid-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Poll cadence: tight at first, then back off. Renders often take several minutes and the
 * server only saves a finished clip when it is polled, so polling runs for up to an hour
 * (it used to stop at 5 min, leaving a slow render stuck on "Rendering…" for good). After
 * that it pauses; `checkNow` asks again and resumes.
 */
export const FAST_INTERVAL = 2000;
export const SLOW_INTERVAL = 5000;
export const LONG_INTERVAL = 15_000;
export const FAST_WINDOW_MS = 20_000;
export const SLOW_WINDOW_MS = 5 * 60_000;
export const MAX_WALL_MS = 60 * 60_000;

/** The poll interval at a given time since submit (or since the last Check now), or false to pause. */
export function pollIntervalFor(sinceMs: number): number | false {
  if (sinceMs > MAX_WALL_MS) return false;
  if (sinceMs < FAST_WINDOW_MS) return FAST_INTERVAL;
  if (sinceMs < SLOW_WINDOW_MS) return SLOW_INTERVAL;
  return LONG_INTERVAL;
}

export interface VideoMutationState {
  /** The in-flight / completed job's id, once submitted. */
  operationId?: string;
  /** Live job snapshot from the poll (or the submit response before the first poll). */
  job?: VideoOperationResult;
  status?: VideoOperationStatus;
  /** True from submit until a terminal status. */
  isRendering: boolean;
  /** Submit-time or terminal failure surfaced for the canvas. */
  error?: VideoOperationFailedError | Error;
  /** Polling paused after MAX_WALL_MS with the job still running; `checkNow` resumes it. */
  stalled: boolean;
}

/**
 * Animation Lab submit-and-poll hook (A-F4). Unlike Image/Writer Lab's
 * synchronous `useOperationMutation`, video resolves to `Queued` and we poll
 * `GET jobs/{id}` until terminal.
 *
 *  - decorates the request with the active studio's `{studioId, metadata}`
 *    (explicit values win), mirroring the image hook;
 *  - polls via TanStack `useQuery` + `refetchInterval` (caching/dedupe/cleanup
 *    for free), enabled only while a job is in flight;
 *  - on Completed: invalidates `['assets']` + session keys so the gallery
 *    refreshes; on Failed/Cancelled: exposes a `VideoOperationFailedError`;
 *  - **cancel-on-unmount**: an abandoned in-flight render still bills, so we
 *    POST `jobs/{id}/cancel` on unmount (cost-safety, A-B10);
 *  - guards double-submit (`isRendering`).
 */
export function useVideoOperationMutation() {
  const qc = useQueryClient();
  const studioRequest = useStudioRequest();
  const { requireSignIn } = useRequireSignIn();

  const [operationId, setOperationId] = useState<string | undefined>();
  const [submitJob, setSubmitJob] = useState<VideoOperationResult | undefined>();
  const [submitError, setSubmitError] = useState<Error | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Refs the unmount cleanup reads without re-subscribing the effect.
  // The wall-clock cap counts from here: submit, or the last Check now.
  const pollWindowRef = useRef<number>(0);
  const [stalled, setStalled] = useState(false);
  const invalidatedRef = useRef(false);
  const liveOpRef = useRef<string | undefined>(undefined);
  const liveTerminalRef = useRef(false);
  // PL-2: one stable idempotency key per logical submit. Reused when the same
  // request is re-submitted (a retry) so the BE returns the original job instead
  // of minting a second, double-charged render; reset when the inputs change.
  const idempotencyRef = useRef<{ sig: string; key: string } | null>(null);

  const pollQuery = useQuery({
    queryKey: queryKeys.videoOperationStatus(operationId ?? ''),
    queryFn: () => VideoOperationService.getStatus(operationId!),
    enabled: !!operationId,
    refetchInterval: query => {
      const status = query.state.data?.status;
      if (isTerminal(status)) return false;
      return pollIntervalFor(Date.now() - pollWindowRef.current);
    },
  });

  const job = pollQuery.data ?? submitJob;
  const status = job?.status;
  const terminal = isTerminal(status);

  // Keep refs in sync for the unmount cleanup.
  liveOpRef.current = operationId;
  liveTerminalRef.current = terminal;

  // Mark the job stalled when the poll window runs out with the job still going.
  useEffect(() => {
    if (!operationId || terminal || stalled) return undefined;
    const left = pollWindowRef.current + MAX_WALL_MS - Date.now();
    const id = setTimeout(() => setStalled(true), Math.max(0, left) + 1);
    return () => clearTimeout(id);
  }, [operationId, terminal, stalled]);

  // On first Completed, refresh the gallery / session caches.
  useEffect(() => {
    if (status === 'Completed' && !invalidatedRef.current) {
      invalidatedRef.current = true;
      const sid = job?.sessionId;
      if (sid) {
        qc.invalidateQueries({ queryKey: queryKeys.session(sid) });
        qc.invalidateQueries({ queryKey: queryKeys.history(sid) });
        qc.invalidateQueries({ queryKey: queryKeys.sessionAssets(sid) });
      }
      qc.invalidateQueries({ queryKey: ['assets'] });
    }
  }, [status, job?.sessionId, qc]);

  const submit = useCallback(
    async (kind: VideoOperationType, request: VideoLabOperationRequest) => {
      if (isSubmitting || (!!operationId && !terminal)) return undefined; // double-submit guard
      // Signed out: the prompt opens and nothing is submitted — not an error.
      if (!requireSignIn()) return undefined;
      setSubmitError(undefined);
      setIsSubmitting(true);
      invalidatedRef.current = false;
      try {
        const decorated: VideoLabOperationRequest = {
          ...request,
          ...(request.studioId === undefined && studioRequest.studioId !== undefined
            ? { studioId: studioRequest.studioId }
            : {}),
          ...(request.metadata === undefined && studioRequest.metadata !== undefined
            ? { metadata: studioRequest.metadata }
            : {}),
        };
        // Stable per logical submit: same inputs (a retry) → same key; changed
        // inputs → fresh key. Signature excludes any pre-existing clientRequestId.
        const { clientRequestId: _drop, ...sigInput } = decorated;
        const sig = JSON.stringify([kind, sigInput]);
        if (idempotencyRef.current?.sig !== sig) {
          idempotencyRef.current = { sig, key: newIdempotencyKey() };
        }
        decorated.clientRequestId = idempotencyRef.current.key;

        const result = await VideoOperationService.submit(kind, decorated);
        pollWindowRef.current = Date.now();
        setStalled(false);
        setSubmitJob(result);
        setOperationId(result.operationId);
        // A job can come back already-terminal (sync failure) — surface it.
        if (result.status === 'Failed' || result.status === 'Cancelled') {
          setSubmitError(new VideoOperationFailedError(result));
        }
        return result;
      } catch (err) {
        const e = err instanceof Error ? err : new Error('Submit failed.');
        setSubmitError(e);
        return undefined;
      } finally {
        setIsSubmitting(false);
      }
    },
    [isSubmitting, operationId, terminal, studioRequest.studioId, studioRequest.metadata, requireSignIn],
  );

  const cancel = useCallback(async () => {
    if (!operationId) return;
    try {
      const result = await VideoOperationService.cancel(operationId);
      setSubmitJob(result);
      qc.setQueryData(queryKeys.videoOperationStatus(operationId), result);
    } catch {
      /* best-effort */
    }
  }, [operationId, qc]);

  /** Ask the server again now and resume polling with a fresh window. */
  const checkNow = useCallback(() => {
    if (!operationId) return;
    pollWindowRef.current = Date.now();
    setStalled(false);
    void pollQuery.refetch();
  }, [operationId, pollQuery]);

  const reset = useCallback(() => {
    setStalled(false);
    setOperationId(undefined);
    setSubmitJob(undefined);
    setSubmitError(undefined);
    invalidatedRef.current = false;
    idempotencyRef.current = null;
  }, []);

  // Cancel-on-unmount — an abandoned in-flight render still bills (cost-safety).
  useEffect(() => {
    return () => {
      const opId = liveOpRef.current;
      if (opId && !liveTerminalRef.current) {
        void VideoOperationService.cancel(opId).catch(() => undefined);
      }
    };
  }, []);

  const terminalError =
    status === 'Failed' || status === 'Cancelled'
      ? new VideoOperationFailedError(job!)
      : undefined;

  const state: VideoMutationState = {
    operationId,
    job,
    status,
    isRendering: (isSubmitting || (!!operationId && !terminal)) && !submitError,
    error: submitError ?? terminalError,
    stalled: stalled && !terminal,
  };

  return { ...state, submit, cancel, reset, checkNow, isSubmitting };
}
